import { LoadingCountdown } from "@package/ui-components";
import { EventPayloadType } from "@package/ui-gql/index-analytics";
import { useWallAnalytics } from "@package/ui-wall";
import { get } from "lodash";
import { useMemo, useState } from "react";
import { type TriviaHelp, useTriviaContext } from "../context-trivia";
import { TriviaButton } from "../TriviaButton/TriviaButton";
import { TriviaQuestionHelp } from "./TriviaQuestionHelp";

export function TriviaQuestion() {
  const { screen, questionIndex, updateTriviaState } = useTriviaContext();
  const { trigger } = useWallAnalytics();
  const [usedHelpTypes, setUsedHelpTypes] = useState<TriviaHelp[]>([]);

  const primaryColorText = useMemo<string>(
    () => get(screen, "flyout.post.customData.primaryColorText"),
    [screen],
  );
  const timeToAnswer = useMemo<number>(
    () => get(screen, "flyout.post.customData.timeToAnswer", 20),
    [screen],
  );
  const question = useMemo<string>(() => {
    const q = get(
      screen,
      `flyout.post.customData.trivia.questions[${questionIndex}].question`,
      "No Question",
    );

    return typeof q === "string" ? q.toUpperCase() : "No Question";
  }, [screen, questionIndex]);
  const answers = useMemo<
    { answer: string; correct: boolean; hidden: boolean; index: number }[]
  >(() => {
    let a: {
      answer: string;
      correct: boolean;
      hidden: boolean;
      index: number;
    }[] = get(
      screen,
      `flyout.post.customData.trivia.questions[${questionIndex}].answers`,
      [],
    );

    if (Array.isArray(a)) {
      a = a.map((answer, index) => ({
        ...answer,
        hidden: false,
        index,
      }));
    }

    if (usedHelpTypes.includes("fityfity")) {
      a = a.slice();

      const totalToRemove = Math.floor(a.length / 2);

      for (
        let i = 0, removed = 0;
        i < a.length && removed < totalToRemove;
        i++
      ) {
        if (!a[i].correct) {
          a[i].hidden = true;
          removed++;
        }
      }
    }

    return a;
  }, [screen, questionIndex, usedHelpTypes]);

  return (
    <div className="flex flex-col h-full w-full">
      <div className="h-3/4 w-full justify-start items-start">
        <div className="h-2/5 w-full flex justify-start items-start">
          <div className="flex flex-col h-full w-full justify-start items-center">
            <div className="flex grow text-4xl justify-center items-center text-center font-bold mt-8 px-8">
              {question}
            </div>
            <div className="flex-none w-full h-48 text-center">
              <LoadingCountdown
                total={timeToAnswer}
                fontSize={28}
                color={primaryColorText}
                onEnd={() => {
                  updateTriviaState((draft) => {
                    draft.questionChoiceIndexes.push(-1);
                    draft.component = "answer";
                  });
                }}
              />
            </div>
          </div>
        </div>
        <div className="h-3/5 w-full flex justify-center items-center">
          <div className="flex flex-wrap w-full pl-16 pr-20">
            {answers
              .filter((answer) => !answer.hidden)
              .map((answer) => (
                <TriviaButton
                  key={answer.index}
                  text={answer.answer}
                  onHandleMouse={() => {
                    updateTriviaState((draft) => {
                      draft.component = "answer";
                      draft.questionChoiceIndexes.push(answer.index);
                    });
                    trigger(
                      EventPayloadType.TriviaQuestion,
                      {
                        postId: screen?.flyout?.post?.id,
                        question: question,
                        answer: answer.answer,
                        correct: answer.correct,
                      },
                      screen?.flyout?.post?.id ?? undefined,
                      screen?.layout?.id ?? undefined,
                    );
                  }}
                />
              ))}
          </div>
        </div>
      </div>
      <div className="h-1/4 w-full justify-center items-center px-20">
        <TriviaQuestionHelp
          onHelpClicked={(help) => {
            setUsedHelpTypes(usedHelpTypes.concat(help));
            updateTriviaState((draft) => {
              draft.availableTriviaHelp = draft.availableTriviaHelp.filter(
                (h) => h !== help,
              );
            });
          }}
        />
      </div>
    </div>
  );
}
